<script lang="ts">
  import { computed, inject, provide, watch, type InjectionKey } from 'vue'
  import { useSessionStorage } from '@vueuse/core'
  import {
    forceReauthentication,
    getAuth0Client,
    getAuthorizationToken
  } from '@gat-ship/auth'
  import { mapToAppError } from '@gat-ship/error-handling'
  import { API_REQUEST_HEADERS } from '../../api-request-headers'

  const SUPPORT_SESSION_KEYS = {
    SESSION_ID: 'Gatship-Support-Session-Id',
    SESSION_TOKEN: 'Gatship-Support-Session-Token'
  } as const
  import { createHttpClient, type HttpClient } from '../../core/http-client'
  import { executeWithAuthRetry } from '../../core/auth-retry'
  import type { RequestConfig } from '../../http-client-types'
  import {
    appSessionId,
    getBaseApiUrl
  } from '../../utils/gs-cloud-backend-utils'
  import { fetchSupportSessionTokenIfExpired } from '../../utils/support-session-token'

  const showDebug = import.meta.env.DEV || import.meta.env.MODE === 'staging'

  function defineGsCloudBackendProvider() {
    const client: HttpClient = createHttpClient({
      baseURL: getBaseApiUrl(),
      defaultHeaders: {
        [API_REQUEST_HEADERS.GATSHIP_APP_SESSION_ID]: appSessionId
      }
    })

    const supportSessionId = useSessionStorage<string | null>(
      SUPPORT_SESSION_KEYS.SESSION_ID,
      null
    )
    const supportSessionToken = useSessionStorage<string | null>(
      SUPPORT_SESSION_KEYS.SESSION_TOKEN,
      null
    )
    const isSupportSession = computed(() => !!supportSessionId.value)

    client.use(async (ctx) => {
      const _authorizationToken = await getAuthorizationToken()
      ctx.headers[API_REQUEST_HEADERS.AUTHORIZATION] =
        `Bearer ${_authorizationToken}`

      if (isSupportSession.value) {
        const _supportSessionToken = await fetchSupportSessionTokenIfExpired({
          supportSessionId: supportSessionId.value,
          currentToken: supportSessionToken.value,
          authorizationToken: _authorizationToken,
          setSupportSessionToken: (token) => {
            supportSessionToken.value = token
          }
        })
        ctx.headers[SUPPORT_SESSION_KEYS.SESSION_TOKEN] = _supportSessionToken
      }
      return ctx
    })

    function setHeader(propertyName: string, value: string) {
      client.setHeader(propertyName, value)
    }

    function setHeaders(headers: Record<string, string>) {
      client.setHeaders(headers)
    }

    /**
     * The only supported way to update the active-tenant header from outside
     * the http-client package. The existing
     * `<GsCloudBackendProvider :database-id="…">` declarative prop remains for
     * nested context overrides (e.g. admin tenant-user impersonation). To
     * clear the header, call
     * `client.removeHeader(API_REQUEST_HEADERS.GATSHIP_ACTIVE_TENANT_DB_INSTANCE)`
     * directly — no caller needs that today.
     */
    function setActiveTenantId(id: string): void {
      client.setHeader(
        API_REQUEST_HEADERS.GATSHIP_ACTIVE_TENANT_DB_INSTANCE,
        id
      )
    }

    function withCommandId(
      commandId: string,
      config?: RequestConfig
    ): RequestConfig {
      return {
        ...config,
        headers: {
          ...config?.headers,
          [API_REQUEST_HEADERS.GATSHIP_COMMAND_ID]: commandId
        }
      }
    }

    async function apiPut<T, U = unknown>(
      path: string,
      payload: U,
      commandId: string,
      config?: RequestConfig
    ): Promise<T> {
      if (showDebug) console.log(`apiPut: ${path} \r\n`, payload)
      try {
        const response = await executeWithAuthRetry(
          () => client.put<T>(path, payload, withCommandId(commandId, config)),
          { getAuthClient: getAuth0Client, forceReauthentication }
        )
        if (showDebug) console.log(`Result ${path}`, response.data)
        return response.data
      } catch (err) {
        throw mapToAppError(err)
      }
    }

    async function apiDelete<T, U = unknown>(
      path: string,
      payload: U,
      commandId: string,
      config?: RequestConfig
    ): Promise<T> {
      if (showDebug) console.log(`apiDelete: ${path}`)
      try {
        const response = await executeWithAuthRetry(
          () =>
            client.delete<T>(path, payload, withCommandId(commandId, config)),
          { getAuthClient: getAuth0Client, forceReauthentication }
        )
        if (showDebug) console.log(`Result ${path}`, response.data)
        return response.data
      } catch (err) {
        throw mapToAppError(err)
      }
    }

    async function apiPost<T, U = unknown>(
      path: string,
      payload: U,
      commandId: string,
      config?: RequestConfig
    ): Promise<T> {
      if (showDebug) console.log(`apiPost: ${path}`)
      try {
        const response = await executeWithAuthRetry(
          () => client.post<T>(path, payload, withCommandId(commandId, config)),
          { getAuthClient: getAuth0Client, forceReauthentication }
        )
        if (showDebug) console.log(`Result ${path}`, response.data)
        return response.data
      } catch (err) {
        throw mapToAppError(err)
      }
    }

    async function apiPatch<T, U = unknown>(
      path: string,
      payload: U,
      commandId: string,
      config?: RequestConfig
    ): Promise<T> {
      if (showDebug) console.log(`apiPatch: ${path} \r\n`, payload)
      try {
        const response = await executeWithAuthRetry(
          () =>
            client.patch<T>(path, payload, withCommandId(commandId, config)),
          { getAuthClient: getAuth0Client, forceReauthentication }
        )
        if (showDebug) console.log(`Result ${path}`, response.data)
        return response.data
      } catch (err) {
        throw mapToAppError(err)
      }
    }

    async function apiGet<T>(path: string, config?: RequestConfig): Promise<T> {
      if (showDebug) console.log(`apiGet: ${path}`)
      try {
        const response = await executeWithAuthRetry(
          () => client.get<T>(path, config),
          { getAuthClient: getAuth0Client, forceReauthentication }
        )
        if (showDebug) console.log(`Result ${path} `, response.data)
        return response.data
      } catch (err) {
        throw mapToAppError(err)
      }
    }

    return {
      client,
      apiGet,
      apiPost,
      apiPut,
      apiPatch,
      apiDelete,
      setHeaders,
      setHeader,
      setActiveTenantId
    }
  }

  export type GsCloudBackendProvider = ReturnType<
    typeof defineGsCloudBackendProvider
  >

  export const gsCloudBackendProviderKey = Symbol(
    'gs-cloud-backend-provider'
  ) as InjectionKey<GsCloudBackendProvider>

  export function useGsCloudBackend() {
    const gsCloudBackendProvider = inject(gsCloudBackendProviderKey)

    if (!gsCloudBackendProvider) {
      throw new Error(
        'could not find gs-cloud-backend-provider. Did you provide it in your app?'
      )
    }
    return gsCloudBackendProvider
  }

  const defaultGsCloudBackendProvider = defineGsCloudBackendProvider()

  const { apiGet, apiPost, apiPut, apiPatch, apiDelete } =
    defaultGsCloudBackendProvider

  export { apiGet, apiPost, apiPut, apiPatch, apiDelete }
</script>
<script setup lang="ts">
  const props = defineProps<{
    /** Do not use this prop. It is for internal use only. */
    internalIsGlobal?: boolean
    databaseId?: string
  }>()

  const gsCloudBackendProvider = props.internalIsGlobal
    ? defaultGsCloudBackendProvider
    : defineGsCloudBackendProvider()

  watch(
    () => props.databaseId,
    (newVal) => {
      if (newVal) {
        gsCloudBackendProvider.setHeader(
          API_REQUEST_HEADERS.GATSHIP_ACTIVE_TENANT_DB_INSTANCE,
          newVal
        )
      }
    }
  )
  provide(gsCloudBackendProviderKey, gsCloudBackendProvider)
  defineExpose(gsCloudBackendProvider)
</script>
<template>
  <slot />
</template>
